import { Component, Input, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { finalize, pairwise, startWith } from 'rxjs/operators';
import { Subscription } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import moment from 'moment';

import { Characteristic } from 'src/app/core/entities/characteristic.entity';
import { ErrorService } from 'src/app/core/services/error.service';
import { GeneticRiskFactorMapping } from 'src/app/core/enums/genetic-risk-factor';
import { Patient } from 'src/app/core/entities/patient.entity';
import { PatientService } from 'src/app/core/services/patient.service';
import { SpinnerService } from 'src/app/core/services/spinner.service';
import { otherRiskMapping } from 'src/app/core/enums/other-risk';
import { relapseMapping } from 'src/app/core/enums/relapse';
import { walkingPerimMapping } from 'src/app/core/enums/walking-perim';

@Component({
  selector: 'app-medical',
  templateUrl: './medical.component.html',
  styleUrls: ['./medical.component.css'],
})
export class MedicalComponent implements OnInit {
  @Input() public patientProfile: Patient = new Patient();
  private _observers: Subscription = new Subscription();
  public medicalForm: FormGroup;
  public submittedMedicalForm: boolean = false;
  public walkingPerimMapping = walkingPerimMapping;
  public relapseMapping = relapseMapping;
  public otherRiskMapping = otherRiskMapping;
  public otherRisks = Object.keys(this.otherRiskMapping);
  public otherRiskFactors: string[] = [];
  public newOtherRiskArray: string[] = [];
  public geneticRiskFactors: string[] = [];
  public newGeneticRiskArray: string[] = [];
  public edssChoice = [];
  public yearsList: any[] = [];
  public geneticRiskMapping = GeneticRiskFactorMapping;
  public geneticRisks = Object.keys(this.geneticRiskMapping);

  get otherRisksFormArray() {
    return this.medicalForm.controls['otherRiskFactors'] as FormArray;
  }

  get geneticRisksFormArray() {
    return this.medicalForm.controls['geneticRiskFactors'] as FormArray;
  }

  constructor(
    private formBuilder: FormBuilder,
    private spinnerService: SpinnerService,
    private toastrService: ToastrService,
    private errorService: ErrorService,
    private patientService: PatientService
  ) {}

  ngOnInit(): void {
    this.prepareMedicalForm();
    this.updateMedicalForm();
    this.addOtherRiskCheckboxes();
    this.addGeneticRiskCheckboxes();
    this.setYearsList();
    this.setObservers();
  }

  setObservers(): void {
    this._observers.add(this.geneticRiskChange());
  }

  private addOtherRiskCheckboxes() {
    const otherRisks = Object.keys(this.otherRiskMapping);
    let risksIncluded = this.patientProfile.otherRiskFactors?.split(',');
    otherRisks.forEach((value, index) => {
      if (risksIncluded?.includes(otherRisks[index])) {
        this.otherRisksFormArray.push(new FormControl(true));
      } else {
        this.otherRisksFormArray.push(new FormControl(false));
      }
    });
  }

  private addGeneticRiskCheckboxes() {
    const geneticRisks = Object.keys(this.geneticRiskMapping);
    let risksIncluded = this.patientProfile.geneticRiskFactors?.split(',');

    geneticRisks.forEach((value, index) => {
      if (risksIncluded?.includes(geneticRisks[index])) {
        this.geneticRisksFormArray.push(new FormControl(true));
      } else {
        this.geneticRisksFormArray.push(new FormControl(false));
      }
    });
  }

  prepareMedicalForm(): void {
    this.medicalForm = this.formBuilder.group({
      msType: [null],
      canWalkOneKm: [null],
      edss: [''],
      edssDate: [null],
      diagnosticYear: [null],
      firstYearRelapseCount: [null],
      firstYearOfSymptoms: [null],
      walkingStickAid: [null],
      crutchesAid: [null],
      walkerAid: [null],
      wheelChairAid: [null],
      walkWithCaregiverAid: [null],
      otherRiskFactors: new FormArray([]),
      geneticRiskFactors: new FormArray([]),
    });
    for (let n = 0; n <= 7; n += 0.5) {
      this.edssChoice.push({ label: n, value: n });
    }
  }

  updateMedicalForm(): void {
    this.medicalForm.patchValue({
      // msType: this.patientProfile.msType,
      canWalkOneKm: this.patientProfile.canWalkOneKm,
      edss: this.patientProfile.edss,
      edssDate: this.patientProfile.edssDate,
      diagnosticYear: this.patientProfile.diagnosticYear,
      firstYearRelapseCount: this.patientProfile.firstYearRelapseCount,
      firstYearOfSymptoms: this.patientProfile.firstYearOfSymptoms,
      walkingStickAid: this.patientProfile.walkingStickAid,
      crutchesAid: this.patientProfile.crutchesAid,
      walkerAid: this.patientProfile.walkerAid,
      wheelChairAid: this.patientProfile.wheelChairAid,
      walkWithCaregiverAid: this.patientProfile.walkWithCaregiverAid,
    });
  }

  geneticRiskChange(): Subscription {
    return this.geneticRisksFormArray.valueChanges
      .pipe(startWith(this.geneticRisksFormArray.value), pairwise())
      .subscribe(([previousData, data]) => {
        if (
          (previousData[0] !== data[0] || previousData[1] !== data[1]) &&
          (data[1] === true || data[0] === true)
        ) {
          this.geneticRisksFormArray.setValue([data[0], data[1], false, false]);
        } else if (previousData[2] !== data[2] && data[2] === true) {
          this.geneticRisksFormArray.setValue([false, false, true, false]);
        } else if (previousData[3] !== data[3] && data[3] === true) {
          this.geneticRisksFormArray.setValue([false, false, false, true]);
        }
      });
  }

  toggleAidStick(): void {
    this.medicalForm.get('walkingStickAid').markAsDirty();
    if (this.medicalForm.get('walkingStickAid').value === true) {
      this.medicalForm.patchValue({ walkingStickAid: false });
    } else {
      this.medicalForm.patchValue({ walkingStickAid: true });
    }
  }
  toggleAidCrutches(): void {
    this.medicalForm.get('crutchesAid').markAsDirty();
    if (this.medicalForm.get('crutchesAid').value === true) {
      this.medicalForm.patchValue({ crutchesAid: false });
    } else {
      this.medicalForm.patchValue({ crutchesAid: true });
    }
  }
  toggleAidWalker(): void {
    this.medicalForm.get('walkerAid').markAsDirty();
    if (this.medicalForm.get('walkerAid').value === true) {
      this.medicalForm.patchValue({ walkerAid: false });
    } else {
      this.medicalForm.patchValue({ walkerAid: true });
    }
  }
  toggleAidWheelchair(): void {
    this.medicalForm.get('wheelChairAid').markAsDirty();
    if (this.medicalForm.get('wheelChairAid').value === true) {
      this.medicalForm.patchValue({ wheelChairAid: false });
    } else {
      this.medicalForm.patchValue({ wheelChairAid: true });
    }
  }
  toggleAidWalkWith(): void {
    this.medicalForm.get('walkWithCaregiverAid').markAsDirty();
    if (this.medicalForm.get('walkWithCaregiverAid').value === true) {
      this.medicalForm.patchValue({ walkWithCaregiverAid: false });
    } else {
      this.medicalForm.patchValue({ walkWithCaregiverAid: true });
    }
  }

  setYearsList(): void {
    let now = moment().utc();
    while (parseInt(now.format('YYYY'), 10) >= 1900) {
      this.yearsList.push(now.format('YYYY'));
      now = now.subtract(1, 'y');
    }
  }

  onSubmit(): void {
    this.spinnerService.show();

    let data = {};
    for (const [key, value] of Object.entries(this.medicalForm.value)) {
      if (this.medicalForm.get(key).pristine) {
        continue;
      }
      if (key === 'geneticRiskFactors') {
        this.newGeneticRiskArray = [];
        Object.keys(this.geneticRiskMapping).forEach((valueMapping, index) => {
          if (value[index]) {
            this.newGeneticRiskArray.push(valueMapping);
          }
        });
        data['geneticRiskFactors'] = this.newGeneticRiskArray.join(',');
      } else if (key === 'otherRiskFactors') {
        this.newOtherRiskArray = [];
        Object.keys(this.otherRiskMapping).forEach((valueMapping, index) => {
          if (value[index]) {
            this.newOtherRiskArray.push(valueMapping);
          }
        });
        data['otherRiskFactors'] = this.newOtherRiskArray.join(',');
      } else {
        data[key] = value;
      }

      this.medicalForm.get(key).markAsPristine();
    }

    this.patientService
      .updateCharacteristics(data, this.patientProfile)
      .pipe(finalize(() => this.spinnerService.hide()))
      .subscribe(
        (res) => {
          Object.entries(res).forEach(([key, value]) => {
            this.patientProfile.updateCharacteristic(value as Characteristic);
          });
          this.toastrService.success(
            $localize`:@@settings_editAccountPatient_SuccessDescription:The patient information are well modified`,
            $localize`:@@settings_editAccount_SuccessTitle:Congratulations`
          );
        },
        (err) => {
          this.errorService.addError(
            $localize`:@@settings_editAccount_ErrorDescription:A problem has occured, please retry`,
            $localize`:@@settings_editAccount_ErrorTitle:We are sorry`
          );
          this.errorService.show();
        }
      );
  }
}
